import { ListingsSortBy, ListingSubtype, ListingType } from 'app/types/listing';
import type { CustomTabFilters } from 'app/schemas/user';
import type { FilterPreferences } from 'app/types/listings-filters';
import type { SpecificNavigationProp, SpecificRouteProp } from 'app/types/navigation';
import { useEffect, type Dispatch, type SetStateAction } from 'react';

/** Apply explicit navigation sorting to the shared controls, then allow manual changes. */
export function useListingsRouteSort({
  sort,
  enabled,
  setSort,
  navigation,
}: {
  sort: ListingsSortBy | undefined;
  enabled: boolean;
  setSort: Dispatch<SetStateAction<ListingsSortBy>>;
  navigation: SpecificNavigationProp<'all-listings'>;
}) {
  useEffect(() => {
    if (!enabled || sort === undefined) return;
    setSort(sort);
    navigation.setParams({ sort: undefined });
  }, [enabled, sort, setSort, navigation]);
}

export function useListingsSorting({
  route,
  navigation,
  appliedFilters,
  forcedFilters,
  type,
  search,
  isLoggedIn,
}: {
  route: SpecificRouteProp<'all-listings'>;
  navigation: SpecificNavigationProp<'all-listings'>;
  appliedFilters: FilterPreferences;
  forcedFilters: CustomTabFilters;
  type: ListingType;
  search: string;
  isLoggedIn: boolean;
}) {
  const listsServicesOnly =
    forcedFilters.subtypes?.length === 1 && forcedFilters.subtypes[0] === ListingSubtype.service;

  useListingsRouteSort({
    sort: route.params?.sort,
    enabled: !isLoggedIn || appliedFilters.isLoaded,
    setSort: listsServicesOnly
      ? appliedFilters.sortServicesState[1]
      : type === ListingType.offer
        ? appliedFilters.sortOffersState[1]
        : appliedFilters.sortNeedsState[1],
    navigation,
  });

  const baseOptions = [...(search.trim().length > 0 ? [ListingsSortBy.Relevance] : [])];

  if (listsServicesOnly) {
    return [...baseOptions, ListingsSortBy.StartDate, ListingsSortBy.Distance, ListingsSortBy.Date];
  }

  if (forcedFilters.type === ListingType.offer) {
    return [
      ...baseOptions,
      ListingsSortBy.Distance,
      ListingsSortBy.Date,
      ListingsSortBy.Popularity,
      ListingsSortBy.Random,
    ];
  }

  return [...baseOptions, ListingsSortBy.Distance, ListingsSortBy.Date, ListingsSortBy.Urgency];
}
